import React from 'react';
import {ApplicationAuthPaths, AuthActions, QueryParameterNames} from './authContstants';
import {getUserManager} from './userManager';
import {SignoutResponse, User} from 'oidc-client';
import {RootState} from 'app/rootReducer';
import {connect, ConnectedProps} from 'react-redux';

type Props = ConnectedProps<typeof connector>;

type State = {
    message: string
};

class AuthHandler extends React.Component<Props, State> {
    readonly state: State  = {
        message: ''
    };

    async componentDidMount() {
        const action = this.props.action;
        switch (action) {
            case AuthActions.Login:
                await this.login();
                break;
            case AuthActions.LoginCallback:
                await this.processLoginCallback();
                break;
            case AuthActions.Logout:
                if (!!window.history.state.state.local) {
                    await this.logout();
                } else {
                    // This prevents regular links to <app>/authentication/logout from triggering a logout
                    this.setState({ message: 'The logout was not initiated from within the page.' });
                }
                break;
            case AuthActions.LogoutCallback:
                await this.processLogoutCallback();
                break;
            case AuthActions.Profile:
                this.redirectToServerPath(ApplicationAuthPaths.IdentityManagePath);
                break;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }
    
    render() {
        return <div>{this.state.message}</div>;
    }

    async login() {
        const {isUserAuthenticated, isLoadingUser} = this.props;
        if (!isUserAuthenticated && !isLoadingUser){
            const userManager = await getUserManager();
            await userManager.signinRedirect(this.createArguments());
        }
    }

    async processLoginCallback() {
        const url = window.location.href;
        const userManager = await getUserManager();
        const user = await userManager.signinCallback(url);
        if (user && user.state) {
            await this.navigateToReturnUrl(this.getReturnUrl(user.state));
        } else {
            this.setState({ message: 'Authentication error' });
        }
    }

    async logout() {
        if (this.props.isUserAuthenticated) {
            const userManager = await getUserManager();
            await userManager.signoutRedirect(this.createArguments());
        } else {
            this.setState({ message: 'You successfully logged out!' });
        }
    }

    async processLogoutCallback() {
        const url = window.location.href;
        const userManager = await getUserManager();
        const result = (await userManager.signoutCallback(url)) as SignoutResponse;
        await this.navigateToReturnUrl(this.getReturnUrl(result?.state));
    }

    getReturnUrl(state?: any) {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            // This is an extra check to prevent open redirects.
            throw new Error('Invalid return url. The return url needs to have the same origin as the current page.')
        }
        return (state && state.returnUrl) || fromQuery || `${window.location.origin}/`;
    }

    createArguments() {
        return { useReplaceToNavigate: true, data: {returnUrl: this.getReturnUrl()}};
    }

    navigateToReturnUrl(returnUrl: string) {
        window.location.replace(returnUrl);
    }
    
    redirectToServerPath(serverPath: string) {
        const redirectUrl = `${window.location.origin}${serverPath}`;
        // It's important that we do a replace here so that when the user hits the back arrow on the
        // browser he gets sent back to where it was on the app instead of to an endpoint on this
        // component.
        window.location.replace(redirectUrl);
    }
}

const mapStateToProps = (state: RootState, ownProps: {action: string}) => {
    const user = state.oidc.user as User;
    return {
        isUserAuthenticated: user && !user.expired,
        isLoadingUser: state.oidc.isLoadingUser,
        ...ownProps
    };
};

const connector = connect(mapStateToProps);
export default connector(AuthHandler);